import React, { useState, useEffect, lazy, Suspense } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getApiBase, authHeaders } from '../utils/organiserSubmissions';

const AuthModal = lazy(() => import('./AuthModal'));

const AccountContainer = styled.div`
  position: relative;
  min-width: 0;
`;

const SignInButton = styled.button`
  background: linear-gradient(135deg, #E9B339, #FF7B5A);
  color: #1f2933;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #d4a233, #e56a4a);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(233, 179, 57, 0.3);
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
`;

const UserDropdown = styled.div`
  position: relative;
  min-width: 0;
`;

const UserButton = styled.button`
  background: var(--surface);
  color: var(--text);
  border: 2px solid var(--border);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  max-width: 13rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: #E9B339;
    background: rgba(233, 179, 57, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
`;

const Username = styled.span`
  display: block;
  min-width: 0;
  max-width: 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownMenu = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--surface);
  border: 2px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 180px;
  overflow: hidden;
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transform: ${props => props.$show ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  margin-top: 0.5rem;
`;

const DropdownItem = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: var(--text);
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  font-size: 0.875rem;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    background: ${props => props.disabled ? 'none' : 'rgba(233, 179, 57, 0.1)'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }

  ${props => props.disabled && `
    cursor: not-allowed;
  `}
`;

const UserIcon = styled.span<{ $verified?: boolean }>`
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #E9B339, #FF7B5A);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  position: relative;
  flex-shrink: 0;

  ${props => !props.$verified && `
    &::after {
      content: '!';
      position: absolute;
      top: -2px;
      right: -2px;
      width: 12px;
      height: 12px;
      background: #ff4444;
      color: white;
      border-radius: 50%;
      font-size: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--surface);
    }
  `}
`;

const ChevronIcon = styled.span<{ $show: boolean }>`
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: ${props => props.$show ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const UserAccount: React.FC = () => {
  const { user, token, isAuthenticated, logout, resendVerification } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const navigate = useNavigate();
  const username = user?.username || 'Account';

  const isAdminEligible = isAuthenticated && user?.verified && user?.isAdmin;

  useEffect(() => {
    if (showDropdown && isAdminEligible && token) {
      setUnreadCount(null);
      let cancelled = false;
      const url = `${getApiBase()}/api/admin/messages?status=new&page=1&pageSize=1`;
      fetch(url, { headers: authHeaders(token) })
        .then((response) => response.ok ? response.json() : null)
        .then((data) => {
          if (!cancelled && data && typeof data.total === 'number') {
            setUnreadCount(data.total);
          }
        })
        .catch(() => {});
      return () => { cancelled = true; };
    }
    setUnreadCount(null);
  }, [showDropdown, isAdminEligible, token, user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-dropdown="user"]')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    logout();
    setShowDropdown(false);
  };

  const handleSavedEvents = () => {
    navigate('/saved-events');
    setShowDropdown(false);
  };

  const handleMyShows = () => {
    navigate('/my-events');
    setShowDropdown(false);
  };

  const handleSettings = () => {
    navigate('/settings');
    setShowDropdown(false);
  };

  const handleAdminDashboard = () => {
    navigate('/admin/submissions');
    setShowDropdown(false);
  };

  const handleMessages = () => {
    navigate('/admin/submissions?section=messages');
    setShowDropdown(false);
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    await resendVerification();
    setIsResending(false);
    setShowDropdown(false);
  };

  if (!isAuthenticated) {
    return (
      <>
        <SignInButton onClick={() => setShowAuthModal(true)}>
          Sign In
        </SignInButton>
        {showAuthModal && (
          <Suspense fallback={null}>
            <AuthModal show={showAuthModal} onHide={() => setShowAuthModal(false)} />
          </Suspense>
        )}
      </>
    );
  }

  return (
    <AccountContainer>
      <UserDropdown data-dropdown="user">
        <UserButton
          onClick={() => setShowDropdown(!showDropdown)}
          aria-label={`User account for ${username}`}
          title={username}
        >
          <UserIcon $verified={user?.verified} aria-hidden="true">
            {username.charAt(0).toUpperCase()}
          </UserIcon>
          <Username aria-hidden="true">{username}</Username>
          <ChevronIcon $show={showDropdown} aria-hidden="true">▼</ChevronIcon>
        </UserButton>

        <DropdownMenu $show={showDropdown}>
          <DropdownItem onClick={handleMyShows}>
            📅 My Events
          </DropdownItem>
          <DropdownItem onClick={handleSavedEvents}>
            ⭐ Saved Events
          </DropdownItem>
          <DropdownItem onClick={handleSettings}>
            ⚙️ Settings
          </DropdownItem>
          {isAdminEligible && (
            <>
              <DropdownItem onClick={handleAdminDashboard}>
                🛡️ Admin dashboard
              </DropdownItem>
              <DropdownItem onClick={handleMessages}>
                <span role="status" aria-live="polite" aria-atomic="true">
                  {unreadCount !== null ? `Messages (${unreadCount} unread)` : 'Messages'}
                </span>
              </DropdownItem>
            </>
          )}
          {!user?.verified && (
            <DropdownItem onClick={handleResendVerification} disabled={isResending}>
              {isResending ? '📧 Sending...' : '📧 Resend Verification'}
            </DropdownItem>
          )}
          <DropdownItem onClick={handleSignOut}>
            🚪 Sign Out
          </DropdownItem>
        </DropdownMenu>
      </UserDropdown>
    </AccountContainer>
  );
};

export default UserAccount;
